// Libraries
import { CellContext } from '@tanstack/react-table';
import { get } from 'lodash';

// Types
import Fund from '@/types/fund';
import Holding from '@/types/holding';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import PortfolioHolding from '@/types/portfolio-holding';
import Stock from '@/types/stock';

// Utils
import { formatLocalizedString, formatMessage } from '@/utils/i18n';
import { HOLDING_TYPES } from '@/utils/portfolios';
import { getCachedCustomYieldFund } from '@/utils/portfolio/customYieldCache';
import Alt from '@/types/models/alt';
import Portfolio from '@/types/portfolio';
import { PORTFOLIO_METRICS_TABLE_COLUMNS } from '@/enums/Portfolio';
import { DISCLAIMER_KEYS } from '@/utils/disclaimers/constants';

export const metricsTableI18nBasePath = 'PortfolioEditorBuilder.Content.MetricsTable';

interface KeyMetrics {
  BETA: Nullable<number>;
  CLASSIFICATION: Nullable<string>;
  CURRENCY: string;
  INCEPTION_DATE: Nullable<string>;
  MER: Nullable<number>;
  OBJECTIVE: Nullable<LocalizedString>;
  ONE_YEAR: Nullable<number>;
  THREE_YEAR: Nullable<number>;
  TNA: {
    value: Nullable<number>;
    currency: Nullable<string>;
  };
}

interface AlternativeOverview {
  COMMITMENT: Nullable<number>;
  CALLED: Nullable<number>;
  DISTRIBUTED: Nullable<number>;
  FIRM_NAME: Nullable<string>;
  GP_COMMITMENTS_TO_FUND: Nullable<number>;
  REMAINING: Nullable<number>;
  STATUS: Nullable<string>;
  VINTAGE: Nullable<number>;
}

interface FeeInformation {
  CARRIED_INTEREST: Nullable<number>;
  CHARGE_FREQUENCY: Nullable<string>;
  DURING_INVESTMENT_PERIOD: Nullable<number>;
  FUND_FORMATION_COST: Nullable<number>;
  GP_CATCH_UP_RATE: Nullable<number>;
  HURDLE_RATE: Nullable<number>;
  POST_INVESTMENT_PERIOD: Nullable<number>;
}

interface AlternativePerformance {
  MEDIAN_BENCHMARK_IRR: Nullable<number>;
  NET_IRR: Nullable<number>;
  QUARTILE: Nullable<string>;
}

export interface Metrics {
  CODE: string | null | undefined;
  NAME: string;
  IS_ALT_HOLDING?: boolean;
  IS_PORTFOLIO?: boolean;
  KEY_METRICS: KeyMetrics;
  ALTERNATIVE_OVERVIEW?: AlternativeOverview;
  FEE_INFORMATION?: FeeInformation;
  ALTERNATIVE_PERFORMANCE?: AlternativePerformance;
}

const isAltSpecificColumn = (columnId: PORTFOLIO_METRICS_TABLE_COLUMNS) =>
  [
    PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME,
    PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS,
    PORTFOLIO_METRICS_TABLE_COLUMNS.COMMITMENT,
    PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND,
    PORTFOLIO_METRICS_TABLE_COLUMNS.CALLED,
    PORTFOLIO_METRICS_TABLE_COLUMNS.DISTRIBUTED,
    PORTFOLIO_METRICS_TABLE_COLUMNS.REMAINING,
    PORTFOLIO_METRICS_TABLE_COLUMNS.DURING_INVESTMENT_PERIOD,
    PORTFOLIO_METRICS_TABLE_COLUMNS.POST_INVESTMENT_PERIOD,
    PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY,
    PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.CARRIED_INTEREST,
    PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.FUND_FORMATION_COST,
    PORTFOLIO_METRICS_TABLE_COLUMNS.NET_IRR,
    PORTFOLIO_METRICS_TABLE_COLUMNS.MEDIAN_BENCHMARK_IRR,
    PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE,
  ].includes(columnId);

const cantAggregateColumn = (columnId: PORTFOLIO_METRICS_TABLE_COLUMNS) =>
  [
    PORTFOLIO_METRICS_TABLE_COLUMNS.CLASSIFICATION,
    PORTFOLIO_METRICS_TABLE_COLUMNS.OBJECTIVE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.INCEPTION_DATE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.TNA,
    PORTFOLIO_METRICS_TABLE_COLUMNS.FIRM_NAME,
    PORTFOLIO_METRICS_TABLE_COLUMNS.VINTAGE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.STATUS,
    PORTFOLIO_METRICS_TABLE_COLUMNS.GP_COMMITMENTS_TO_FUND,
    PORTFOLIO_METRICS_TABLE_COLUMNS.CHARGE_FREQUENCY,
    PORTFOLIO_METRICS_TABLE_COLUMNS.HURDLE_RATE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.GP_CATCH_UP_RATE,
    PORTFOLIO_METRICS_TABLE_COLUMNS.QUARTILE,
  ].includes(columnId);

export const getDisclaimer = (info: CellContext<Metrics, any>) => {
  const { row, column } = info;
  const isAltHolding = row.original.IS_ALT_HOLDING;
  const isPortfolioRow = row.original.IS_PORTFOLIO;

  if (isPortfolioRow && cantAggregateColumn(column.id as PORTFOLIO_METRICS_TABLE_COLUMNS)) {
    return DISCLAIMER_KEYS.NOT_AGGREGATED;
  }

  if (isAltHolding === undefined) {
    return undefined;
  }

  return !isAltHolding && isAltSpecificColumn(column.id as PORTFOLIO_METRICS_TABLE_COLUMNS) ? DISCLAIMER_KEYS.NOT_APPLICABLE : undefined;
};

export const getCellValue = (info: CellContext<Metrics, any>) => info.getValue() ?? null;

export const getHeader = (i18nKey: string): string => formatMessage(`${metricsTableI18nBasePath}.Columns.${i18nKey}`);

export const getMetricValue = <T extends string | number = string | number>(obj: Holding | Portfolio, path: string): Nullable<T> => {
  const rawValue = get(obj, path, null);

  if (rawValue === null || rawValue === undefined) {
    return null as Nullable<T>;
  }

  // TODO: Remove or adjust when investigation of CONN-8477 is complete
  if (typeof rawValue === 'number' && Number.isNaN(rawValue)) {
    return null as Nullable<T>;
  }

  return rawValue as Nullable<T>;
};

export const getLocalizedHoldingValue = (holding: Holding, value: string) => get(holding, value, null) as Nullable<LocalizedString>;

export const getLocalizedAndFormattedHoldingValue = (holding: Holding, value: string) => {
  const localizedValue = getLocalizedHoldingValue(holding, value);
  return localizedValue ? formatLocalizedString(localizedValue) : null;
};

export const getTNA = (holding: Holding) => {
  const tna = get(holding, 'tna.value', null) as Nullable<number>;
  const currency = get(holding, 'tna.currency', null) as Nullable<string>;
  return { value: tna, currency };
};

type GetHoldingData = (
  holding: PortfolioHolding,
  getFundData: (id: string) => Promise<Fund>,
  getStockData: (id: string) => Promise<Stock>,
  getAltData: (id: string) => Promise<Alt>,
) => Promise<Holding>;

export const getHoldingData: GetHoldingData = (holding, getFundData, getStockData, getAltData) => {
  if (!holding.original.id) {
    return Promise.reject();
  }

  switch (holding.original.type) {
    case HOLDING_TYPES.FUND: {
      return getFundData(holding.original.id);
    }
    case HOLDING_TYPES.STOCK: {
      return getStockData(holding.original.id);
    }
    case HOLDING_TYPES.ALT: {
      return getAltData(holding.original.id);
    }
    case HOLDING_TYPES.CUSTOM_YIELD: {
      if (!holding.original.yieldParams) {
        return Promise.reject();
      }

      return getCachedCustomYieldFund(
        holding.original.yieldParams,
        { filteredShelves: holding.filteredShelves, complianceComparisons: holding.complianceComparisons },
        holding.currency,
        holding.original.id,
      );
    }
    default: {
      return Promise.reject();
    }
  }
};
